<template>
    <div>
        <div :class="[
            'w-full flex items-center justify-between py-4 border-b border-border-darker transition-transform duration-250 ease-in-out',
        ]">
            <p class="text-xl xl:text-2xl font-header font-semibold">
                {{ name }}
            </p>
        </div>

        <UserOrganizationPresentation v-for="item in items.slice(0, 2)" :modelClass="item.modelClass" :name="item.name"
            :imageSrc="item.first_image?.preview_url" :parentUrl="item.parentUrl" :parentName="item.parentName"
            :url="item.url" />
        <p v-if="items.length == 0" class="py-3 pl-4 italic font-medium text-text-secondary"
            v-text="noApprovedItemsMsg" />

        <p v-if="items.length > 2" v-text="$lang.get('... :count more', { count: items.length - 2 })"
            class="py-3 pl-16 text-text-secondary" />

        <div class="w-full flex flex-col items-end pt-4 border-t border-border-darker/20">
            <button @click="modalOpened = true" class="flex items-center gap-x-2 underline-hover">
                <p v-text="openModalButtonText || $lang.get('Show all')" />
                <PhArrowRight class="size-6" />
            </button>
        </div>
        <AffiliationModal @reloadRelations="$emit('reloadRelations')" @sendInvites="
            (usersToInvite) => $emit('sendInvites', usersToInvite)
        " @close="modalOpened = false" @removeItem="(item_id) => $emit('removeItem', item_id)" :show="modalOpened"
            :title="modalTitle" :items="items" :sentRequests="sentRequests" :recievedRequests="recievedRequests"
            :filterTabNames="filterTabNames" :canEdit="canEdit" :invitesEnabled="invitesEnabled"
            :approveAffiliationText="approveAffiliationText" :definitiveItemName="definitiveItemName"
            :definitiveRecievedName="definitiveRecievedName" :definitiveSentName="definitiveSentName"
            :alreadyConfirmedMsg="alreadyConfirmedMsg" :alreadySentMsg="alreadySentMsg"
            :alreadyRequestedMsg="alreadyRequestedMsg" :inviteText="inviteText" :initialTab="activeTab"
            :sendInviteText="sendInviteText" :removeButtonText="removeButtonText" :onlyConfirmed="onlyConfirmed"
            :isManagingEmployees="isManagingEmployees" :parentModel="parentModel" :isInQueue="isInQueue" />
    </div>
</template>

<script>
import ModelPreview from '@/Components/ModelComponents/ModelPreview.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import UserOrganizationPresentation from '@/Components/ModelComponents/UserOrganizationPresentation.vue'
import AffiliationModal from '@/Components/Panels/AffiliationPanel/AffiliationModal.vue'
import { Link } from '@inertiajs/vue3'
import { PhArrowRight } from '@phosphor-icons/vue'

export default {
    name: 'AffiliationPanel',

    emits: ['reloadRelations', 'removeItem', 'sendInvites'],

    components: {
        ModelPreview,
        VerticalSlideTransition,
        UserOrganizationPresentation,
        Link,
        AffiliationModal,
        // Icons
        PhArrowRight,
    },

    props: {
        name: {
            type: String,
            required: true,
        },
        // The model that this panel is show on
        parentModel: {
            type: Object,
            required: true,
        },
        items: {
            type: Array,
            default: [],
        },
        sentRequests: {
            type: Array,
            default: [],
        },
        recievedRequests: {
            type: Array,
            default: [],
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        openModalButtonText: {
            type: [String, null],
            default: null,
        },
        modalTitle: {
            type: [String, null],
            default: null,
        },
        invitesEnabled: {
            type: Boolean,
            default: false,
        },
        filterTabNames: {
            type: Object,
            default: {},
            validator: (value, props) => {
                if (props.onlyConfirmed && !value) {
                    return true
                }
                return Object.keys(value).every((key) =>
                    ['all', 'approved', 'recievedRequests'].includes(key)
                ) && props.invitesEnabled
                    ? Object.hasOwn(value, 'sentRequests')
                    : true
            },
        },
        approveAffiliationText: {
            type: String,
            default: '',
            validator: (value, props) => {
                if (props.onlyConfirmed || !props.invitesEnabled) {
                    return true
                }
                return !!value
            },
        },
        definitiveItemName: {
            type: String,
            required: true,
        },
        definitiveRecievedName: {
            type: String,
            default: '',
            validator: (value, props) => {
                if (props.onlyConfirmed || !props.invitesEnabled) {
                    return true
                }
                return !!value
            },
        },
        definitiveSentName: {
            type: String,
            default: '',
            validator: (value, props) => {
                if (props.onlyConfirmed || !props.invitesEnabled) {
                    return true
                }
                return !!value
            },
        },
        alreadyConfirmedMsg: {
            type: String,
            default: '',
            validator: (value, props) => {
                if (props.onlyConfirmed || !props.invitesEnabled) {
                    return true
                }
                return !!value
            },
        },
        alreadySentMsg: {
            type: String,
            default: '',
            validator: (value, props) => {
                if (props.onlyConfirmed || !props.invitesEnabled) {
                    return true
                }
                return !!value
            },
        },
        alreadyRequestedMsg: {
            type: String,
            default: '',
            validator: (value, props) => {
                if (props.onlyConfirmed || !props.invitesEnabled) {
                    return true
                }
                return !!value
            },
        },

        noApprovedItemsMsg: {
            type: String,
            required: true,
        },
        openOnLoad: {
            type: Boolean,
            default: false,
        },
        activeTab: {
            type: String,
            default: null,
        },
        inviteText: {
            type: String,
            default: null,
        },
        removeButtonText: {
            type: String,
            default: null,
        },
        sendInviteText: {
            type: String,
            default: null,
        },
        onlyConfirmed: {
            type: Boolean,
            default: false,
        },
        isManagingEmployees: {
            type: Boolean,
            default: false
        },
        isInQueue: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            modalOpened: false,
        }
    },

    mounted() {
        this.$nextTick(() => {
            if (this.openOnLoad) {
                this.modalOpened = true
            }
        })
    },
}
</script>
